import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import Select from "components/common/Select";
import EventService from "service/event/EventService";
import { maskPhone } from "util/maskPhone";
import { NAME_MAX, nameError, nameErrors, nameFilter } from "../helper/nameRules";
import PatientService from "../service/PatientService";
import { PD_V2_REFRESH } from "../sections/patientDetails/events";

/**
 * Patient details — px-matched to "Popup · Patient details" (3015:5175,
 * 560×443), opened from the patient name / chevron in the appointment header.
 *
 * It is a CENTRED MODAL, not a popover: the board carries no caret and no
 * anchor data, and the designer labelled the neighbouring 360-wide component
 * "popover" while calling this one "Popup".
 *
 * Five editable fields (First/Last name, Gender, Date of birth,
 * Communication email) all persist through ONE call — bridge's
 * `PUT /v1/patients/profile/update`, a full-record replace.
 *
 * TWO FIELDS ARE SHOWN BUT NOT EDITABLE, and both are greyed so the form does
 * not invite a change that would go nowhere:
 *   · Work email has no field in that payload — nor on the patient record at
 *     all. It lives in api-client-config-service and is READ here, on open
 *     (PatientService.getWorkEmail). It used to be read off `patient.workEmail`,
 *     a field the record never carries, so it showed "—" for everyone
 *     (2026-09-21). Shown only: deactivating it is Bridge Profile's action.
 *   · Mobile number IS in the payload — a full-record replace has to carry it —
 *     but nothing in the estate edits it through this endpoint: every bridge
 *     caller echoes `String(patient.phoneno)` back unchanged, and the one screen
 *     with a typeable phone field is the patient-CREATE form, on a different
 *     endpoint.
 *
 * Save is disabled until something actually changes (the board ships the
 * button in its #d1d5dc disabled state; the sibling variant 3015:5229 shows
 * the #6a5ef5 enabled one) — and while a MANDATORY field is empty.
 *
 * The MOBILE NUMBER IS MASKED (product, 2026-09-17): "+91 XXXXX X9876", only
 * the last four digits shown — on screen and in its tooltip. The full number
 * is still what the save sends back.
 *
 * SAVE SAYS HOW IT WENT (2026-09-17): a toast on success, and on failure a
 * toast as well as the inline message. Through EventService "showError", which
 * both apps' hosts listen for — phr's ToastHost renders tone "success" green,
 * bridge's GlobalError shows its one snackbar — the same call TherapyPicker
 * makes, so this file stays identical in phr and bridge.
 *
 * MANDATORY: First name, Gender and Date of birth, each marked with a red
 * asterisk (product, 2026-09-16). The mark and the gate are one list
 * (REQUIRED below): an asterisk with nothing behind it is decoration, and a
 * gate with no asterisk is a Save button that stays grey for no visible reason.
 */

// The record reads gender as an int: 2 = Male, 1 = Female (bridge's mapping).
const GENDERS = [
  { key: 2, label: "Male" },
  { key: 1, label: "Female" },
];

const toDateInput = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};

const asText = (v) => (v === null || v === undefined ? "" : String(v));

/** The fields Save waits on — and the ones whose labels carry the asterisk. */
const REQUIRED = {
  firstName: (form) => !!asText(form.firstName).trim(),
  gender: (form) => GENDERS.some((g) => g.key === form.gender),
  dateOfBirth: (form) => !!form.dateOfBirth,
};

/**
 * The wire body. Full-record replace: every field the endpoint knows about
 * must be present or the server clears it. `phoneno` → `phoneNo` (String),
 * `dateOfBirth` → "YYYY-MM-DD".
 */
export const buildProfileUpdateBody = (patient, form) => ({
  patientId: patient.id,
  firstName: form.firstName.trim(),
  lastName: form.lastName.trim(),
  gender: form.gender,
  email: form.email.trim(),
  dateOfBirth: form.dateOfBirth || "",
  phoneNo: asText(form.phoneNo).trim(),
  countryCode: patient.countryCode,
  profileImage: patient.profileImage,
  age: patient.age,
});

const PatientDetailsDialog = ({ patient, onClose, onSaved }) => {
  const initial = useMemo(
    () => ({
      firstName: asText(patient.firstName),
      lastName: asText(patient.lastName),
      phoneNo: asText(patient.phoneno),
      gender: patient.gender,
      dateOfBirth: toDateInput(patient.dateOfBirth),
      email: asText(patient.email),
    }),
    [patient]
  );

  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  /* Set when Save is pressed, like the app — which validates the payload on
     submit rather than while the reviewer is still typing — and cleared per
     field as soon as what is in it becomes a name. */
  const [nameErr, setNameErr] = useState({});

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  /* The work email: "loading", then its address (and whether it has been
     deactivated), or "none" (404 "no work email found for patient" — echo's
     body carries no code, so the message is what says so), or "error". */
  const [work, setWork] = useState({ state: "loading" });
  useEffect(() => {
    let live = true;
    new PatientService().getWorkEmail(
      patient.id,
      (res) => {
        if (!live) return;
        setWork(
          res && res.workEmail
            ? { state: "loaded", email: String(res.workEmail), off: res.status === 2 }
            : { state: "none" }
        );
      },
      (e) => {
        if (!live) return;
        const none =
          Number(e && e.code) === 404 || /no work email/i.test(String((e && e.message) || ""));
        setWork({ state: none ? "none" : "error" });
      }
    );
    return () => {
      live = false;
    };
  }, [patient.id]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !submitting && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  const set = (k) => (v) => {
    /* A name is FILTERED as it is typed, the way the app filters it. */
    const next = k === "firstName" || k === "lastName" ? nameFilter(k, v) : v;
    setForm((prev) => ({ ...prev, [k]: next }));
    /* And its error goes the moment the value is acceptable — leaving it up
       while the reviewer fixes the field is the app's one rough edge here
       (it re-validates only on the next Save). */
    if (k === "firstName" || k === "lastName") {
      setNameErr((prev) => (prev[k] && !nameError(k, next) ? { ...prev, [k]: "" } : prev));
    }
  };

  const dirty = useMemo(
    () => Object.keys(initial).some((k) => form[k] !== initial[k]),
    [form, initial]
  );
  const complete = Object.keys(REQUIRED).every((k) => REQUIRED[k](form));
  const canSave = dirty && !submitting && complete;

  const submit = () => {
    if (!canSave) return;
    /* THE NAMES ARE CHECKED HERE, not by greying Save out: a button that will
       not press says something is wrong and refuses to say what. */
    const bad = nameErrors(form);
    setNameErr(bad);
    if (Object.keys(bad).length) return;
    setSubmitting(true);
    setFeedback(null);
    const svc = new PatientService();
    svc.updatePatientProfile(
      buildProfileUpdateBody(patient, form),
      (res) => {
        if (!mounted.current) return;
        setSubmitting(false);
        EventService.emit("showError", { message: "Patient details updated", tone: "success" });
        // Broadcast the fresh patient so the section-rail Patient
        // details table can re-seed its `live` state without waiting
        // for the parent's refetchPatient to round-trip. Also picked up
        // by components/PatientDetails.js so `patient` state updates
        // even when the parent's normal onPatientUpdated path is not
        // wired (defence in depth — the header dialog's own onSaved
        // still handles the parent refetch).
        const fresh = { ...patient, ...form, phoneno: form.phoneNo };
        EventService.emit(PD_V2_REFRESH, fresh);
        if (onSaved) onSaved(fresh, res);
      },
      (err) => {
        if (!mounted.current) return;
        setSubmitting(false);
        const message = (err && err.message) || "Couldn't save — please retry";
        setFeedback(message);
        EventService.emit("showError", { message: `Couldn't save patient details: ${message}` });
      }
    );
  };

  const field = (label, node, required, error) => (
    <div className="pdx-field">
      <label className="pdx-lbl">
        {label}
        {required ? (
          <span className="pdx-req" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      {node}
      {error ? <p className="pdx-err">{error}</p> : null}
    </div>
  );

  const text = (k, extra) => (
    <input
      className="pdx-input"
      type="text"
      value={form[k]}
      onChange={(e) => set(k)(e.target.value)}
      disabled={submitting}
      {...extra}
    />
  );

  return (
    <div className="cc-modal-backdrop pdx-backdrop" onMouseDown={submitting ? null : onClose}>
      <div
        className="pdx"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pdx-title"
      >
        <header className="pdx-head">
          <div className="pdx-head-text">
            <h2 id="pdx-title" className="pdx-title">Patient details</h2>
            <p className="pdx-sub">
              PID {patient.id} &middot; changes sync to the patient record
            </p>
          </div>
          <button
            type="button"
            className="pdx-x"
            onClick={submitting ? null : onClose}
            disabled={submitting}
            aria-label="Close"
          >
            <svg viewBox="0 0 28 28" fill="none" aria-hidden="true">
              <path d="M8.5 8.5L19.5 19.5M19.5 8.5L8.5 19.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        </header>

        <div className="pdx-body">
          <div className="pdx-row">
            {field(
              "First name",
              text("firstName", {
                "aria-label": "First name",
                "aria-required": true,
                "aria-invalid": !!nameErr.firstName,
                maxLength: NAME_MAX,
              }),
              true,
              nameErr.firstName
            )}
            {field(
              "Last name",
              text("lastName", {
                "aria-label": "Last name",
                "aria-invalid": !!nameErr.lastName,
                maxLength: NAME_MAX,
              }),
              false,
              nameErr.lastName
            )}
          </div>

          <div className="pdx-row">
            {field(
              "Mobile number",
              /* NOT EDITABLE, and now it looks it. The profile-update endpoint
                 is a full-record replace, so the number has to be SENT — but no
                 caller anywhere edits it: every bridge use of this endpoint
                 echoes `String(patient.phoneno)` straight back, and the one
                 screen with a typeable phone field is the patient-CREATE form,
                 a different endpoint. It was rendered as a live input, so it
                 invited a change that would have gone nowhere.

                 form.phoneNo is left in place rather than removed: it still
                 feeds the body and onSaved, and since nothing mutates it now it
                 can never make the form look dirty. */
              <div
                className="pdx-input is-readonly"
                aria-disabled="true"
                title={maskPhone(patient.countryCode, form.phoneNo) || "Not recorded"}
              >
                {maskPhone(patient.countryCode, form.phoneNo) || "—"}
              </div>
            )}
            {field(
              "Gender",
              <Select
                value={form.gender}
                options={GENDERS}
                getKey={(o) => o.key}
                getLabel={(o) => o.label}
                onChange={set("gender")}
                disabled={submitting}
                menuClassName="pdx-menu"
              />,
              true
            )}
          </div>

          <div className="pdx-row">
            {field(
              "Date of birth",
              <div className="pdx-date">
                <input
                  className="pdx-input"
                  type="date"
                  value={form.dateOfBirth}
                  onChange={(e) => set("dateOfBirth")(e.target.value)}
                  max={new Date().toISOString().slice(0, 10)}
                  /* Native picker won't let the reviewer scroll below
                     Jan 1900. Blocks fat-finger '0202-06-…' from
                     landing as a real, distant-past date. Mirrors the
                     section-rail form. */
                  min="1900-01-01"
                  disabled={submitting}
                  aria-label="Date of birth"
                  aria-required="true"
                />
                <span className="pdx-date-icon" aria-hidden="true">
                  {/* viewBox extended to y=16 — the path draws down to
                      y=15.625, so a `0 0 15 15` box clipped the
                      calendar's bottom row (visible in the section
                      rail; subtler here but still wrong). */}
                  <svg viewBox="0 0 15 16" fill="none">
                    <path
                      d="M12.1875 3.125H11.5625V1.875H10.3125V3.125H4.0625V1.875H2.8125V3.125H2.1875C1.5 3.125 0.9375 3.6875 0.9375 4.375V14.375C0.9375 15.0625 1.5 15.625 2.1875 15.625H12.1875C12.875 15.625 13.4375 15.0625 13.4375 14.375V4.375C13.4375 3.6875 12.875 3.125 12.1875 3.125ZM12.1875 14.375H2.1875V7.5H12.1875V14.375ZM12.1875 6.25H2.1875V4.375H12.1875V6.25Z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
              </div>,
              true
            )}
            {field("Communication email", text("email", { type: "email", "aria-label": "Communication email" }))}
          </div>

          <div className="pdx-row is-single">
            {/* Board's only muted value: the profile-update payload has no
                work-email field, so this is shown, not edited. A deactivated
                one is still shown, marked as such, so it never reads as a live
                corporate link. */}
            {field(
              "Work email",
              /* aria-disabled, so the greying is not the only signal — a
                 screen reader is told this one cannot be edited too. */
              <div
                className="pdx-input is-readonly"
                aria-disabled="true"
                title={
                  work.state === "loaded"
                    ? `${work.email}${work.off ? " (deactivated)" : ""}`
                    : work.state === "error"
                    ? "Couldn't load the work email"
                    : work.state === "loading"
                    ? "Loading…"
                    : "Not recorded"
                }
              >
                <span className="pdx-ro-text">
                  {work.state === "loading" ? "Loading…" : work.email || "—"}
                </span>
                {work.off ? <span className="pdx-ro-tag">Deactivated</span> : null}
              </div>
            )}
          </div>

          {feedback && <p className="pdx-err">{feedback}</p>}
        </div>

        <footer className="pdx-foot">
          <button type="button" className="pdx-cancel" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="button" className="pdx-save" onClick={submit} disabled={!canSave}>
            {submitting ? "Saving…" : "Save changes"}
          </button>
        </footer>
      </div>
    </div>
  );
};

PatientDetailsDialog.propTypes = {
  patient: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
};

/* Re-exported so a screen that already imports this dialog does not need a
   second import for the rules it applies. helper/nameRules is the source. */
export { NAME_MAX, nameError, nameErrors, nameFilter };

export default PatientDetailsDialog;
